import type { ServiceResponse } from './types';
import { apiRequest } from '../utils/api';
import { API_ENDPOINTS } from '../config/api.config';

// Backend response types
interface CouponValidationResponseDto {
  isValid: boolean;
  coupon?: {
    code: string;
    discountType: 'percentage' | 'fixed';
    discountValue: number;
    minimumOrderAmount?: number;
    maximumDiscount?: number;
    description?: string;
  };
  message?: string;
}

interface ApplyCouponResponseDto {
  originalAmount: number;
  discountAmount: number;
  finalAmount: number;
  couponCode: string;
  message: string;
}

// Frontend types
export interface CouponValidation {
  isValid: boolean;
  code?: string;
  discountType?: 'percentage' | 'fixed';
  discountValue?: number;
  minimumOrderAmount?: number;
  maximumDiscount?: number;
  description?: string;
  message?: string;
}

export interface AppliedCoupon {
  originalAmount: number;
  discountAmount: number;
  finalAmount: number;
  couponCode: string;
  message: string;
}

export interface CouponValidationRequest {
  code: string;
  orderAmount?: number;
  customerId?: string;
  productIds?: string[];
  categoryIds?: string[];
  customerTier?: string;
  isFirstTimeCustomer?: boolean;
  productQuantities?: Record<string, number>;
}

export interface ApplyCouponRequest {
  code: string;
  orderAmount: number;
  orderItems?: Array<{
    productId: string;
    quantity: number;
    price: number;
  }>;
}

class CouponService {
  // Validate a coupon code
  async validateCoupon(request: CouponValidationRequest): Promise<ServiceResponse<CouponValidation>> {
    try {
      // The endpoint expects the code as a URL parameter
      const endpoint = `/coupons/validate/${encodeURIComponent(request.code)}`;
      
      // Build the request body according to ValidateCouponDto
      const body: any = {
        customerId: request.customerId || 'guest', // Default to guest if no customer ID
        orderTotal: request.orderAmount || 0
      };
      
      // Add optional fields if provided
      if (request.productIds) body.productIds = request.productIds;
      if (request.categoryIds) body.categoryIds = request.categoryIds;
      if (request.customerTier) body.customerTier = request.customerTier;
      if (request.isFirstTimeCustomer !== undefined) body.isFirstTimeCustomer = request.isFirstTimeCustomer;
      if (request.productQuantities) body.productQuantities = request.productQuantities;
      
      const response = await apiRequest<CouponValidationResponseDto>(
        endpoint,
        {
          method: 'POST',
          body: JSON.stringify(body),
        }
      );

      // The backend response includes the validation result directly
      const backendResponse = response as any;
      
      const validation: CouponValidation = {
        isValid: backendResponse.isValid || false,
        message: backendResponse.message,
        code: request.code.toUpperCase()
      };

      // Check if we have discount information
      if (backendResponse.discountAmount !== undefined) {
        // Backend provides calculated discount amount
        const discountAmount = backendResponse.discountAmount;
        
        // Calculate discount percentage if order amount is provided
        if (request.orderAmount && discountAmount > 0) {
          const discountPercentage = Math.round((discountAmount / request.orderAmount) * 100);
          
          // Determine if it's percentage or fixed based on the calculation
          if (discountPercentage === 10 || discountPercentage === 20 || discountPercentage === 25 || 
              discountPercentage === 30 || discountPercentage === 50) {
            // Common percentage discounts
            validation.discountType = 'percentage';
            validation.discountValue = discountPercentage;
          } else {
            // Fixed amount discount
            validation.discountType = 'fixed';
            validation.discountValue = discountAmount;
          }
        } else {
          // Default to fixed amount
          validation.discountType = 'fixed';
          validation.discountValue = discountAmount;
        }
      } else if (backendResponse.coupon) {
        // Handle response with coupon details
        validation.discountType = backendResponse.coupon.discountType || 'fixed';
        validation.discountValue = backendResponse.coupon.discountValue || 0;
        validation.minimumOrderAmount = backendResponse.coupon.minimumOrderAmount;
        validation.maximumDiscount = backendResponse.coupon.maximumDiscount;
        validation.description = backendResponse.coupon.description;
      }

      return {
        success: true,
        data: validation
      };
    } catch (error) {
      console.error('Error validating coupon:', error);
      return {
        success: false,
        data: {
          isValid: false,
          message: error instanceof Error ? error.message : 'Invalid coupon code'
        },
        error: error instanceof Error ? error.message : 'Failed to validate coupon'
      };
    }
  }

  // Apply a coupon to an order
  async applyCoupon(request: ApplyCouponRequest): Promise<ServiceResponse<AppliedCoupon>> {
    try {
      const response = await apiRequest<ApplyCouponResponseDto>(
        API_ENDPOINTS.COUPONS.APPLY,
        {
          method: 'POST',
          body: JSON.stringify(request),
        }
      );

      return {
        success: true,
        data: {
          originalAmount: response.originalAmount,
          discountAmount: response.discountAmount,
          finalAmount: response.finalAmount,
          couponCode: response.couponCode,
          message: response.message
        }
      };
    } catch (error) {
      console.error('Error applying coupon:', error);
      return {
        success: false,
        data: null as unknown as AppliedCoupon,
        error: error instanceof Error ? error.message : 'Failed to apply coupon'
      };
    }
  }

  // Calculate discount locally (for preview)
  calculateDiscount(
    orderAmount: number,
    discountType: 'percentage' | 'fixed',
    discountValue: number,
    minimumOrderAmount?: number,
    maximumDiscount?: number
  ): { discountAmount: number; finalAmount: number; isEligible: boolean; message?: string } {
    // Check minimum order amount
    if (minimumOrderAmount && orderAmount < minimumOrderAmount) {
      return {
        discountAmount: 0,
        finalAmount: orderAmount,
        isEligible: false,
        message: `Minimum order amount of ₹${minimumOrderAmount} required`
      };
    }

    let discountAmount = 0;

    if (discountType === 'percentage') {
      discountAmount = (orderAmount * discountValue) / 100;
      
      // Apply maximum discount cap if specified
      if (maximumDiscount && discountAmount > maximumDiscount) {
        discountAmount = maximumDiscount;
      }
    } else {
      // Fixed discount
      discountAmount = discountValue;
      
      // Ensure discount doesn't exceed order amount
      if (discountAmount > orderAmount) {
        discountAmount = orderAmount;
      }
    }

    const finalAmount = orderAmount - discountAmount;

    return {
      discountAmount,
      finalAmount,
      isEligible: true
    };
  }

  // Format discount display
  formatDiscount(discountType: 'percentage' | 'fixed', discountValue: number): string {
    if (discountType === 'percentage') {
      return `${discountValue}% OFF`;
    } else {
      return `₹${discountValue} OFF`;
    }
  }

  // Store applied coupon in session
  storeAppliedCoupon(couponCode: string): void {
    sessionStorage.setItem('applied_coupon', couponCode);
  }

  // Get stored coupon
  getStoredCoupon(): string | null {
    return sessionStorage.getItem('applied_coupon');
  }

  // Clear stored coupon
  clearStoredCoupon(): void {
    sessionStorage.removeItem('applied_coupon');
  }
}

export const couponService = new CouponService();